import { useCallback, useEffect, useRef, useState } from 'react';
import { useAction, useApiQuery } from '../../../engine/api/hooks';
import { ApiError } from '../../../engine/api/client';
import { useDataSource } from '../../../engine/meta/useDataSource';

// The workspace's timer (docs/servicedesk-v2-proposal.md P6). The browser owns the observation —
// it knows whether the tab is visible and whether anyone is typing — and reports ACTIVE seconds to
// the API on a heartbeat. Nothing is billed by this: the technician confirms the minutes when they
// log, and the server rounds under the tenant's (or the contract's) policy.

export interface CaptureSession {
  id: string;
  ticket_id: string | null;
  technician_id: string;
  source: string;
  started_at: string;
  ended_at: string | null;
  captured_seconds: number;
  idle_seconds: number;
  note: string | null;
  time_entry_id: string | null;
}

export interface DayGap {
  session_id: string;
  ticket_id: string | null;
  ticket_ref: string | null;
  ticket_summary: string | null;
  started_at: string;
  ended_at: string | null;
  captured_seconds: number;
  suggested_minutes: number;
  running: boolean;
}

export interface DayView {
  date: string;
  technician_id: string;
  rounding: { round_to_minutes: number; minimum_minutes: number };
  logged_minutes: number;
  captured_minutes: number;
  unlogged_minutes: number;
  billable_minutes: number;
  gaps: DayGap[];
  entries: Array<{ id: string; ticket_id: string | null; ticket_ref: string | null; minutes: number; billable: boolean; charge_type: string; note: string | null; created_at: string }>;
}

/** How often the browser reports what it saw. Short enough to survive a crash, long enough to be quiet. */
const HEARTBEAT_MS = 60_000;
/** No interaction for this long and the tab stops counting — idle is not work. */
const IDLE_MS = 120_000;

export function useRunningCapture(enabled = true) {
  // Not on a connected system that does not serve time capture (Halo: time goes on the Halo ticket).
  const served = useDataSource().resourceServed('time-captures');
  return useApiQuery<{ data: CaptureSession | null }>(['time-capture', 'open'], '/v1/time-captures/open', { enabled: enabled && served });
}

export function useDay(date: string, enabled = true) {
  // The day's boundaries are the viewer's, not the server's: send the local day as instants.
  const from = new Date(`${date}T00:00:00`).toISOString();
  const to = new Date(`${date}T23:59:59.999`).toISOString();
  return useApiQuery<DayView>(['time-capture', 'day', date], `/v1/time-captures/day?date=${date}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { enabled });
}

export interface TimerApi {
  session: CaptureSession | null;
  running: boolean;
  /** Captured seconds including the part this tab has seen but not yet reported. */
  seconds: number;
  start: () => void;
  stop: () => void;
  busy: boolean;
  /** The organisation's tickets live in a connected system (e.g. Halo): the local timer does not
   *  apply there (the API answers 501) — time is logged on the ticket's conversation instead. */
  unavailable: boolean;
}

/**
 * Drive the timer for one ticket. Counts only while the tab is visible and the person is alive at
 * the keyboard; flushes to the server every minute and on unmount, so closing a laptop loses at
 * most one heartbeat (and the server's sweep ends the session anyway).
 */
export function useTicketTimer(ticketId: string, opts: { autoStart?: boolean } = {}): TimerApi {
  const open = useRunningCapture();
  const startAction = useAction<CaptureSession>('time-captures', 'start', { toast: { success: false } });
  const stopAction = useAction<CaptureSession>('time-captures', 'stop', { toast: { success: false } });
  const beat = useAction<CaptureSession>('time-captures', 'heartbeat', { toast: { success: false } });

  const session = open.data?.data ?? null;
  const mine = session && session.ticket_id === ticketId && !session.ended_at ? session : null;
  const [pending, setPending] = useState(0); // active seconds seen but not yet reported
  const lastActive = useRef(Date.now());
  const sessionId = mine?.id ?? null;

  // Only real presence counts: a visible tab plus a person who has done something recently.
  useEffect(() => {
    if (!sessionId) return;
    const mark = () => { lastActive.current = Date.now(); };
    const evs: Array<keyof WindowEventMap> = ['keydown', 'pointerdown', 'wheel', 'focus'];
    for (const e of evs) window.addEventListener(e, mark);
    const tick = window.setInterval(() => {
      const idle = Date.now() - lastActive.current > IDLE_MS;
      if (!idle && document.visibilityState === 'visible') setPending((p) => p + 1);
    }, 1000);
    return () => {
      for (const e of evs) window.removeEventListener(e, mark);
      window.clearInterval(tick);
    };
  }, [sessionId]);

  // `beat`/`open` are new objects every render; reading them through refs keeps `flush` stable so
  // the interval effect below runs once per session instead of on every render (which flushed —
  // one heartbeat plus one refetch — every second).
  const beatRef = useRef(beat);
  beatRef.current = beat;
  const openRef = useRef(open);
  openRef.current = open;
  const flush = useCallback(
    (seconds: number) => {
      if (!sessionId || seconds <= 0) return;
      setPending(0);
      beatRef.current.mutate({ id: sessionId, body: { active_seconds: seconds } }, { onSuccess: () => void openRef.current.refetch() });
    },
    [sessionId],
  );

  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useEffect(() => {
    if (!sessionId) return;
    const iv = window.setInterval(() => flush(pendingRef.current), HEARTBEAT_MS);
    return () => {
      window.clearInterval(iv);
      flush(pendingRef.current); // leaving the ticket must not lose the minute in hand
    };
  }, [sessionId, flush]);

  const start = useCallback(() => {
    startAction.mutate({ body: { ticket_id: ticketId, source: 'workspace' } }, { onSuccess: () => void open.refetch() });
  }, [open, startAction, ticketId]);

  const stop = useCallback(() => {
    if (!sessionId) return;
    flush(pendingRef.current);
    stopAction.mutate({ id: sessionId, body: {} }, { onSuccess: () => void open.refetch() });
  }, [flush, open, sessionId, stopAction]);

  const unavailable = open.error instanceof ApiError && open.error.status === 501;
  const started = useRef(false);
  useEffect(() => {
    if (!opts.autoStart || started.current || open.isLoading || mine || unavailable) return;
    started.current = true;
    start();
  }, [mine, open.isLoading, opts.autoStart, start, unavailable]);

  return {
    session: mine,
    running: Boolean(mine),
    seconds: (mine?.captured_seconds ?? 0) + pending,
    start,
    stop,
    busy: startAction.isPending || stopAction.isPending,
    unavailable,
  };
}

export function formatSeconds(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}
